import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  Subscription,
  catchError,
  filter,
  interval,
  of,
  startWith,
  switchMap,
} from 'rxjs';

interface AppVersion {
  build: string;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private loadedBuild?: string;
  private prompted = false;
  private started = false;
  private subscription = new Subscription();

  constructor(
    private http: HttpClient,
    private notification: NzNotificationService
  ) {}

  start() {
    if (this.started) {
      return;
    }

    this.started = true;

    this.subscription.add(
      interval(POLL_INTERVAL_MS)
        .pipe(
          startWith(0),
          switchMap(() => this.fetchBuildId()),
          filter((build): build is string => !!build)
        )
        .subscribe((build) => this.handleBuildId(build))
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private fetchBuildId() {
    return this.http
      .get<AppVersion>(`/assets/version.json?t=${Date.now()}`)
      .pipe(
        catchError(() => of(null)),
        switchMap((payload) => of(payload?.build ?? null))
      );
  }

  private handleBuildId(build: string) {
    if (!this.loadedBuild) {
      this.loadedBuild = build;
      return;
    }

    if (build !== this.loadedBuild) {
      this.promptReload();
    }
  }

  private promptReload() {
    if (this.prompted) {
      return;
    }

    this.prompted = true;

    const ref = this.notification.info(
      'Nova versão disponível',
      'Clique nesta notificação para recarregar a página.',
      {
        nzDuration: 0,
        nzPlacement: 'topRight',
        nzKey: 'app-update',
      }
    );

    ref.onClick.subscribe(() => window.location.reload());
  }
}
